import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export interface AnnouncementDefaultFontFamilyOptions {
  getDefaultFontFamily: () => string;
}

// Guarantees every text run always carries an explicit textStyle.fontFamily,
// even when the user never touched the font picker. Without this, a
// plain-typed run has no font-family mark at all, so it inherits the app's
// global body font ("Poppins" — see App.css) instead of one of the uploaded
// TTFonts the backend actually knows about. The backend has no notion of
// "Poppins" — for any run it resolves a null fontId to Java's logical
// SansSerif font — so an unstyled run would render (and measure, for line
// wrapping/height) in a completely different typeface than the preview.
// Forcing a real, known font id onto every run keeps editor and preview
// using the exact same font file on both sides.
export const AnnouncementDefaultFontFamily =
  Extension.create<AnnouncementDefaultFontFamilyOptions>({
    name: 'announcementDefaultFontFamily',

    addOptions() {
      return {
        getDefaultFontFamily: () => '',
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('announcementDefaultFontFamily'),
          appendTransaction: (transactions, _oldState, newState) => {
            const defaultFontFamily = this.options.getDefaultFontFamily();
            if (!defaultFontFamily) {
              return null;
            }
            if (!transactions.some((transaction) => transaction.docChanged)) {
              return null;
            }

            const textStyleType = newState.schema.marks.textStyle;
            if (!textStyleType) {
              return null;
            }

            const { tr } = newState;
            let modified = false;
            let hasText = false;

            newState.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) {
                return;
              }
              hasText = true;

              const existingMark = textStyleType.isInSet(node.marks);
              if (existingMark?.attrs.fontFamily) {
                return;
              }

              const attrs = {
                ...existingMark?.attrs,
                fontFamily: defaultFontFamily,
              };
              tr.addMark(pos, pos + node.nodeSize, textStyleType.create(attrs));
              modified = true;
            });

            if (!hasText) {
              const storedMarks =
                tr.storedMarks ??
                newState.storedMarks ??
                newState.selection.$from.marks();
              const existingStoredMark = textStyleType.isInSet(storedMarks);
              if (!existingStoredMark?.attrs.fontFamily) {
                const attrs = {
                  ...existingStoredMark?.attrs,
                  fontFamily: defaultFontFamily,
                };
                tr.setStoredMarks([
                  ...storedMarks.filter((mark) => mark.type !== textStyleType),
                  textStyleType.create(attrs),
                ]);
                modified = true;
              }
            }

            return modified ? tr : null;
          },
        }),
      ];
    },
  });
